import { AlertTriangle, X, Upload, PenLine, ExternalLink } from 'lucide-react';
import { tr } from '../lib/i18nHelpers';
import { workflowWarningDef, type WorkflowWarningKind } from '../lib/workflow-warnings';

// Warning detail modal (2026-10-22 spec §5.4) — renders the dictionary entry
// from workflow-warnings.ts for a backend warning kind: title / What's wrong
// 發生了什麼 / Why it matters 影響 / What you can do 你可以這樣做, ported from
// the workflow-home-demo.html mockup. Width max-w-[820px] (owner-approved).
//
// The modal only emits callbacks — the parent (workflow pages) owns closing
// after a navigate/gap-prompt so it can sequence modal swaps.
export default function WorkflowWarningModal({ kind, targetId, detail, reviewUrl, onClose, onNavigate, onGapPrompt }: {
  kind: WorkflowWarningKind;
  targetId?: string;
  detail?: string;
  /** Optional item review URL — only the Review upload page passes it, for the
   *  type-agnostic review_pending kind (statement/invoice kinds derive their
   *  own URLs from targetId). */
  reviewUrl?: string;
  onClose: () => void;
  onNavigate: (to: string) => void;
  onGapPrompt: () => void;
}) {
  const def = workflowWarningDef(kind, { targetId, detail, reviewUrl });

  // Action icons mirror the mockup's rotation (upload / pen-line / external-link).
  const ACTION_ICONS = [Upload, PenLine, ExternalLink];

  const sectionHeader = (en: string, hant: string, hans: string) => (
    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{tr(en, hant, hans)}</div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-[820px] mx-4 max-h-[85vh] overflow-y-auto p-6"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold">{def.title}</h3>
              {/* Instance-specific line from the backend, when provided */}
              {detail && <p className="text-sm text-muted-foreground mt-0.5">{detail}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded shrink-0" aria-label={tr('Close', '關閉', '关闭')}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Three sections — What's wrong / Why it matters / What you can do */}
        <div className="space-y-3 text-sm">
          <div>
            {sectionHeader("What's wrong", '發生了什麼', '发生了什么')}
            <div>{def.what}</div>
          </div>
          <div>
            {sectionHeader('Why it matters', '影響', '影响')}
            <div className="text-muted-foreground">{def.why}</div>
          </div>
          <div>
            {sectionHeader('What you can do', '你可以這樣做', '你可以这样做')}
            <div className="space-y-1.5">
              {def.actions.map((action, i) => {
                const Icon = ACTION_ICONS[i % ACTION_ICONS.length];
                return (
                  <button
                    key={`${action.kind}-${i}`}
                    onClick={() => {
                      if (action.kind === 'navigate') onNavigate(action.to);
                      else if (action.kind === 'gap-prompt') onGapPrompt();
                      else action.callback();
                    }}
                    className="w-full flex items-center gap-2 border rounded-md px-3 py-2 hover:bg-muted text-left"
                  >
                    <Icon className="h-4 w-4 text-primary shrink-0" />
                    {action.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-md hover:bg-muted">
            {tr('Close', '關閉', '关闭')}
          </button>
        </div>
      </div>
    </div>
  );
}
